import { dateRange, escapeHtml, fullName, locationText, mdToHtml, type TailoredResume } from "./markdown";

export const meta = { id: "executive", name: "Executive" };

const CSS = `
  :root { --ink: #111; --muted: #444; --faint: #666; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #eee; color: var(--ink);
    font-family: "Palatino Linotype", "Book Antiqua", Palatino, Georgia, serif;
    font-size: 13px; line-height: 1.4; }
  .page { max-width: 8.5in; margin: 22px auto; background: #fff; padding: 0.7in 0.75in; }
  header { text-align: center; margin-bottom: 16px; }
  h1 { font-size: 23px; font-weight: 700; text-transform: uppercase; margin: 0; }
  .title { font-size: 18px; font-style: italic; color: var(--muted); margin: 8px 0 9px; }
  .contact { font-size: 12px; color: var(--muted); }
  .summary { margin-bottom: 4px; }
  .summary p { margin: 0; }
  section { margin-top: 15px; }
  h2 { font-size: 15px; font-weight: 700; text-transform: uppercase; margin: 0 0 6px; }
  .body p { margin: 0 0 5px; }
  .body ul { margin: 3px 0 6px; padding-left: 17px; }
  .body li { margin: 2px 0; }
  .entry { margin-bottom: 11px; }
  .entry:last-child { margin-bottom: 0; }
  .head { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
  .role { font-size: 14px; font-weight: 700; }
  .role .org { font-weight: 400; font-style: italic; }
  .dates { font-size: 12px; color: var(--faint); white-space: nowrap; }
  .place { font-size: 12px; color: var(--faint); margin: 1px 0 3px; }
  .rel { font-size: 12px; color: var(--muted); margin-top: 2px; }
  .rel .label { font-weight: 700; }
  .rel ul { display: inline; margin: 0; padding: 0; }
  .rel li { display: inline; }
  .rel li:not(:last-child)::after { content: ", "; }
  .inline { font-size: 13px; }
  .ref { margin-bottom: 6px; }
  .ref .name { font-weight: 700; }
  .ref .meta { font-size: 12px; color: var(--faint); }
  @media print {
    body { background: #fff; }
    .page { margin: 0; max-width: none; padding: 0.5in 0.6in; }
  }
`;

const has = (v: unknown) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
const section = (title: string, inner: string) =>
	has(inner) ? `<section><h2>${title}</h2><div class="body">${inner}</div></section>` : "";

function header(r: TailoredResume) {
	const c = (r.contact || {}) as TailoredResume["contact"];
	const line = [c.email, c.phone, locationText(c.location)].filter(Boolean).map(escapeHtml).join("  ·  ");
	return `<header>
      <h1>${escapeHtml(fullName(c))}</h1>
      ${c.title ? `<div class="title">${escapeHtml(c.title)}</div>` : ""}
      ${line ? `<div class="contact">${line}</div>` : ""}
    </header>
    ${has(r.summary) ? `<div class="summary body">${mdToHtml(r.summary)}</div>` : ""}`;
}

const experience = (list: any[] | undefined) =>
	(list || [])
		.map(
			(j) => `<div class="entry">
      <div class="head"><span class="role">${escapeHtml(j.jobTitle || "")}${j.employer ? `<span class="org"> — ${escapeHtml(j.employer)}</span>` : ""}</span><span class="dates">${escapeHtml(dateRange(j))}</span></div>
      ${locationText(j.location) ? `<div class="place">${escapeHtml(locationText(j.location))}</div>` : ""}
      ${has(j.highlights) ? mdToHtml(j.highlights) : ""}
      ${has(j.relevantSkills) ? `<div class="rel"><span class="label">Core technologies:</span> ${mdToHtml(j.relevantSkills)}</div>` : ""}
    </div>`,
		)
		.join("");

const education = (list: any[] | undefined) =>
	(list || [])
		.map(
			(ed) => `<div class="entry">
      <div class="head"><span class="role">${escapeHtml(ed.educationLevel || ed.fieldOfStudy || "")}${ed.schoolName ? `<span class="org"> — ${escapeHtml(ed.schoolName)}</span>` : ""}</span><span class="dates">${escapeHtml(ed.graduation || "")}</span></div>
      ${locationText(ed.location) ? `<div class="place">${escapeHtml(locationText(ed.location))}</div>` : ""}
      ${has(ed.details) ? mdToHtml(ed.details) : ""}
    </div>`,
		)
		.join("");

const languages = (list: any[] | undefined) =>
	`<div class="inline">${(list || []).map((l) => `${escapeHtml(l.language)}${l.proficiency ? ` — ${escapeHtml(l.proficiency)}` : ""}`).join("  ·  ")}</div>`;

const linksList = (list: any[] | undefined) =>
	(list || []).map((l) => `<div>${l.type ? escapeHtml(l.type) + ": " : ""}${escapeHtml(l.value)}</div>`).join("");

const references = (list: any[] | undefined) =>
	(list || [])
		.map(
			(rf) => `<div class="ref">
      <span class="name">${escapeHtml(rf.name || "")}</span>${rf.designation ? `, ${escapeHtml(rf.designation)}` : ""}${rf.workPlace ? ` — ${escapeHtml(rf.workPlace)}` : ""}
      ${[rf.phone, rf.email].filter(Boolean).length ? `<div class="meta">${[rf.phone, rf.email].filter(Boolean).map(escapeHtml).join("  ·  ")}</div>` : ""}
    </div>`,
		)
		.join("");

const customSections = (list: any[] | undefined) =>
	(list || []).map((s) => section(escapeHtml(s.title), has(s.content) ? mdToHtml(s.content) : "")).join("");

export function renderBody(r: TailoredResume): string {
	return [
		header(r),
		section("Areas of Expertise", has(r.skills) ? mdToHtml(r.skills) : ""),
		section("Professional Experience", experience(r.workExperience)),
		section("Education", education(r.education)),
		section("Certifications", has(r.certifications) ? mdToHtml(r.certifications) : ""),
		customSections(r.customSections),
		section("Accomplishments", has(r.accomplishments) ? mdToHtml(r.accomplishments) : ""),
		section("Professional Affiliations", has(r.affiliations) ? mdToHtml(r.affiliations) : ""),
		section("Languages", has(r.languages) ? languages(r.languages) : ""),
		section("Additional Information", has(r.additionalInfo) ? mdToHtml(r.additionalInfo) : ""),
		section("Links", has(r.personalLinks) ? linksList(r.personalLinks) : ""),
		section("References", references(r.references)),
	].join("");
}

export function renderDocument(resume: TailoredResume): string {
	return `<!doctype html><html><head><meta charset="utf-8" /><style>${CSS}</style></head><body><div class="page" id="resume">${renderBody(resume)}</div></body></html>`;
}
